import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Chip,
} from '@mui/material'
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Label as LabelIcon,
} from '@mui/icons-material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Tag } from '@/api/tagsApi'
import { formatDateDMY } from '@/utils/date'

interface TagCardProps {
  tag: Tag
  onEdit: (tag: Tag) => void
  onDelete?: (tag: Tag) => void
  canDelete?: boolean
  /** Клик по карточке — переход к словам тега. */
  onOpen?: (tag: Tag) => void
}

export function TagCard({ tag, onEdit, onDelete, canDelete = true, onOpen }: TagCardProps) {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isSystem = tag.owner_id == null

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  // Menu лежит внутри Card в дереве React, а синтетические события React
  // всплывают через портал по дереву, а не по DOM. Без остановки здесь клик по
  // пункту меню и клик по подложке уводили бы на список слов тега.
  const handleEdit = (event: React.MouseEvent) => {
    event.stopPropagation()
    handleMenuClose()
    onEdit(tag)
  }

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation()
    handleMenuClose()
    onDelete?.(tag)
  }

  const wordsCount = tag.words_count ?? 0
  const color = tag.color ?? '#6c757d'

  return (
    <Card
      onClick={onOpen ? () => onOpen(tag) : undefined}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: onOpen ? 'pointer' : 'default',
        '&:hover': {
          boxShadow: 3,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: 1,
                  backgroundColor: color,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              />
              <Typography variant="h6" component="h3">
                {tag.name}
              </Typography>
              {tag.owner_id == null && (
                <Chip
                  size="small"
                  label={t('tags.systemTag')}
                  variant="outlined"
                  sx={{ ml: 0.5 }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LabelIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {t('tags.wordsCount')}: {wordsCount}
              </Typography>
            </Box>
            {tag.created_at && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {t('tags.createdAt')}: {formatDateDMY(tag.created_at)}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={handleMenuOpen} sx={{ ml: 'auto' }}>
            <MoreVertIcon />
          </IconButton>
        </Box>
      </CardContent>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        slotProps={{ root: { onClick: (event: React.MouseEvent) => event.stopPropagation() } }}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          {t('common.edit')}
        </MenuItem>
        {onDelete && canDelete && !isSystem && (
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            {t('common.delete')}
          </MenuItem>
        )}
      </Menu>
    </Card>
  )
}
